/**
 * Research service — AI prospect research for a lead.
 *
 * runProspectResearch() calls the native /api/research endpoint (Claude + web search), which returns
 * one combined markdown blob with `## PROPERTY / OWNER / NEIGHBORHOOD / FIT ASSESSMENT` sections and a
 * bold Fit verdict. saveLeadResearch() parses that into the four section records and stores them in the
 * Research table (replace-on-rerun), matching the legacy model. getLeadResearch() reads them back.
 *
 * Auth: the endpoint accepts a Clerk bearer token (added alongside its legacy shared-token path) so the
 * browser never holds a server secret.
 */
import { apiGet, apiPost, apiDeleteBatch } from './_api.js'

const TBL_RESEARCH = 'tblG309Ef71gxNxgU'

const SECTION_LABELS = [
  { label: 'PROPERTY', type: 'Property' },
  { label: 'OWNER', type: 'Owner' },
  { label: 'NEIGHBORHOOD', type: 'Neighborhood' },
  { label: 'FIT ASSESSMENT', type: 'Fit Assessment' },
]

// Races a promise against a plain timeout — used so a hanging Clerk token fetch or a stalled
// network connection can never leave the caller waiting forever with no feedback (the bug this
// guards against: "Run research" got stuck on "Researching…" indefinitely with no error, no
// server-side request even landing in Vercel's logs — a silently hung connection, not a slow one).
function withTimeout(promise, ms, timeoutValue) {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(timeoutValue), ms)
    promise.then(v => { clearTimeout(timer); resolve(v) }, () => { clearTimeout(timer); resolve(timeoutValue) })
  })
}

function clerkToken() {
  try { return window.__clerkGetToken ? window.__clerkGetToken() : Promise.resolve(null) } catch { return Promise.resolve(null) }
}

const RESEARCH_TIMEOUT_MS = 240000   // 4 min — generous headroom under api/research.js's 300s maxDuration

/** Run the AI research (single coherent pass — see api/research.js). Returns { result: markdown } or { error }. */
export async function runProspectResearch({ fullName, fullAddress, additionalContact, organization, referredByName, projectDescription }) {
  const token = await withTimeout(clerkToken(), 5000, null)
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = 'Bearer ' + token

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), RESEARCH_TIMEOUT_MS)
  try {
    const res = await fetch('/api/research', {
      method: 'POST', headers, signal: controller.signal,
      body: JSON.stringify({ fullName, fullAddress, additionalContact, organization, referredByName, projectDescription }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return { error: data.error || `Research failed (${res.status})` }
    return data   // { result }
  } catch (e) {
    if (e && e.name === 'AbortError') return { error: 'Research timed out after 4 minutes. Please try again.' }
    return { error: (e && e.message) || 'Research request failed' }
  } finally {
    clearTimeout(timer)
  }
}

/** Split the combined markdown into { Property, Owner, Neighborhood, 'Fit Assessment' } content blocks. */
function splitSections(md) {
  const out = {}
  const text = String(md || '')
  for (let i = 0; i < SECTION_LABELS.length; i++) {
    const { label, type } = SECTION_LABELS[i]
    const re = new RegExp('##\\s*' + label.replace(' ', '\\s*') + '\\s*([\\s\\S]*?)(?=\\n##\\s|$)', 'i')
    const m = re.exec(text)
    if (m && m[1].trim()) out[type] = m[1].trim()
  }
  return out
}

/** Pull the bold Fit verdict (e.g. "Strong Fit") from the Fit Assessment text. */
export function parseFitBadge(fitText) {
  const m = /\*\*\s*(Strong Fit|Good Fit|Potential Fit|Weak Fit|Poor Fit)\s*\*\*/i.exec(String(fitText || ''))
  return m ? m[1].replace(/\b\w/g, c => c.toUpperCase()) : ''
}

function normResearch(r) {
  const f = (r && r.fields) || {}
  return {
    id: r.id,
    type: f['Research Type'] || '',
    content: f['Research Content'] || '',
    fitBadge: f['Fit Badge'] || '',
    date: f['Research Date'] || null,
    subject: f['Subject'] || '',
  }
}

/** Fetch research records by id (the lead's `Research` link ids). Returns them in section order. */
export async function getLeadResearch(researchIds) {
  const ids = Array.isArray(researchIds) ? researchIds.filter(Boolean) : []
  if (!ids.length) return []
  const out = []
  for (let i = 0; i < ids.length; i += 50) {
    const chunk = ids.slice(i, i + 50)
    const formula = 'OR(' + chunk.map(id => `RECORD_ID()='${id}'`).join(',') + ')'
    const recs = await apiGet(TBL_RESEARCH, 'filterByFormula=' + encodeURIComponent(formula))
    out.push(...recs.map(normResearch))
  }
  const order = SECTION_LABELS.map(s => s.type)
  return out.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type))
}

/**
 * Replace a lead's research with the parsed sections of `markdown`.
 * @param {string} leadId
 * @param {string} markdown  combined result from runProspectResearch
 * @param {string} subject   display subject (lead name / address)
 * @param {string[]} priorIds existing research record ids to delete first
 * @returns {Promise<Array>} the new normalized research records
 */
export async function saveLeadResearch(leadId, markdown, subject, priorIds) {
  if (Array.isArray(priorIds) && priorIds.length) {
    try { await apiDeleteBatch(TBL_RESEARCH, priorIds) } catch (_) { /* non-fatal */ }
  }
  const sections = splitSections(markdown)
  const today = new Date().toISOString().slice(0, 10)
  const created = []
  for (const { type } of SECTION_LABELS) {
    const content = sections[type]
    if (!content) continue
    const fields = {
      'Research Type': type,
      'Research Content': content,
      'Subject': subject || '',
      'Research Date': today,
      'Leads': [leadId],
    }
    if (type === 'Fit Assessment') fields['Fit Badge'] = parseFitBadge(content)
    const res = await apiPost(TBL_RESEARCH, fields, { typecast: true })
    if (res && res.id) created.push(normResearch(res))
  }
  return created
}
